import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { RefreshCw, AlertCircle, ExternalLink, Globe, Download, Copy, CheckCheck, Link2 } from 'lucide-react';
import { BACKEND_URL } from '../utils/api';
import { toast } from 'sonner';

// Demo mode: when true, share-preview minting is completely disabled.
// Preview renders purely client-side from files[] (srcDoc). No DB / auth dependency.
const DISABLE_SHARE_PREVIEW = String(process.env.REACT_APP_DISABLE_SHARE_PREVIEW || "").toLowerCase() === "true";

/**
 * StaticPreview - Phase 2 Implementation with HTTP Preview Support
 * ================================================================
 * Dumb preview renderer. Does NOT assume React.
 * 
 * Phase 1 HTTP Preview:
 * - When projectId is provided, shows "Open in Browser" button
 * - HTTP preview URL: /api/projects/{projectId}/preview/{path}
 * - Requires user to be authenticated (opens in new tab)
 * 
 * Logic:
 * 1. Receive files array
 * 2. Find index.html
 * 3. Render in iframe (srcDoc)
 * 4. Let browser handle the rest
 * 
 * If no valid index.html -> show error
 * No magic, no guessing.
 */
const StaticPreview = ({ files, artifacts, projectId }) => {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [currentPage, setCurrentPage] = useState('/index.html');
    const iframeRef = useRef(null);
    const backendBaseUrl = BACKEND_URL;
    const [shareUrl, setShareUrl] = useState(null);
    const [shareExpiresAt, setShareExpiresAt] = useState(null);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareError, setShareError] = useState(null);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const fetchShareUrl = useCallback(async (projId, options = {}) => {
        if (DISABLE_SHARE_PREVIEW) return;   // demo mode — skip DB call
        if (!projId) return;
        if (!options.silent) {
            setShareLoading(true);
        }
        setShareError(null);
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await fetch(`${backendBaseUrl}/api/projects/${projId}/preview/share`, {
                headers,
            });
            let data = null;
            try {
                data = await response.json();
            } catch (err) {
                data = null;
            }
            if (!response.ok || !data?.url) {
                throw new Error(data?.detail || `Failed to mint preview link (${response.status})`);
            }
            setShareUrl(data.url);
            const expiresAt = Date.now() + (data.expires_in || 0) * 1000;
            setShareExpiresAt(expiresAt);
        } catch (err) {
            console.error('Preview share link error:', err);
            setShareUrl(null);
            setShareExpiresAt(null);
            setShareError(err.message || 'Failed to mint share link');
        } finally {
            if (!options.silent) {
                setShareLoading(false);
            }
        }
    }, [backendBaseUrl]);

    useEffect(() => {
        if (DISABLE_SHARE_PREVIEW) return;   // demo mode — no share link
        if (!projectId) {
            setShareUrl(null);
            setShareExpiresAt(null);
            return;
        }
        fetchShareUrl(projectId);
    }, [projectId, fetchShareUrl]);

    useEffect(() => {
        if (DISABLE_SHARE_PREVIEW) return;   // demo mode — no refresh
        if (!projectId || !shareExpiresAt) return;
        const msLeft = shareExpiresAt - Date.now();
        const buffer = 30000; // Refresh 30s before expiry
        if (msLeft <= buffer) {
            fetchShareUrl(projectId, { silent: true });
            return;
        }
        const timer = setTimeout(() => fetchShareUrl(projectId, { silent: true }), msLeft - buffer);
        return () => clearTimeout(timer);
    }, [projectId, shareExpiresAt, fetchShareUrl]);

    const remainingShareSeconds = shareExpiresAt ? Math.max(0, Math.floor((shareExpiresAt - now) / 1000)) : null;
    const shareExpiryLabel = shareUrl && remainingShareSeconds !== null
        ? (remainingShareSeconds > 60
            ? `${Math.ceil(remainingShareSeconds / 60)}m`
            : `${remainingShareSeconds}s`)
        : null;
    const shareAlmostExpired = shareUrl && remainingShareSeconds !== null && remainingShareSeconds <= 30;

    // Build virtual filesystem from files or artifacts
    const buildFileSystem = () => {
        const fs = new Map();

        // If we have artifacts in the new contract format, use them
        // Guard: artifacts.pages must be non-empty — an empty array (e.g. fallback builds)
        // must fall through to the files-array branch so CSS/JS are rendered.
        if (artifacts && artifacts.pages && Array.isArray(artifacts.pages) && artifacts.pages.length > 0) {
            for (const page of artifacts.pages) {
                const path = (page.path || '').replace(/^\//, '');
                if (path && page.html) {
                    fs.set(path, {
                        content: page.html,
                        css: page.css || '',
                        js: page.js || ''
                    });
                }
            }
            // V6.2.2: Synthesize standalone styles.css / main.js from per-page
            // CSS/JS so inlineLocalAssets can resolve <link href="styles.css">.
            if (!fs.has('styles.css')) {
                const allCss = Array.from(fs.values()).map(e => e.css).filter(Boolean).join('\n');
                if (allCss) fs.set('styles.css', { content: allCss, css: '', js: '' });
            }
            if (!fs.has('main.js')) {
                const allJs = Array.from(fs.values()).map(e => e.js).filter(Boolean).join('\n');
                if (allJs) fs.set('main.js', { content: allJs, css: '', js: '' });
            }
        }
        // Otherwise use files array
        else if (Array.isArray(files)) {
            for (const file of files) {
                const path = (file.path || '').replace(/^\//, '');
                const content = file.content || '';
                if (path) {
                    fs.set(path, { content, css: '', js: '' });
                }
            }
        }

        return fs;
    };

    // Find index.html in filesystem
    const findIndexHtml = (fs) => {
        // Check common variations
        const candidates = ['index.html', 'Index.html', 'INDEX.html'];
        for (const candidate of candidates) {
            if (fs.has(candidate)) {
                return { path: candidate, ...fs.get(candidate) };
            }
        }
        return null;
    };

    // Inline local CSS/JS references so srcDoc iframes render correctly.
    // Replaces <link href="styles.css"> with <style>...contents...</style>
    // and <script src="main.js"> with <script>...contents...</script>
    // V6.2.2: Falls back to absolute backend URL for any un-resolved local assets.
    const inlineLocalAssets = (html, fs) => {
        // Inline CSS: <link ... href="X.css" ...>
        html = html.replace(
            /<link\b[^>]*\bhref=["']([^"']+\.css)["'][^>]*\/?>/gi,
            (match, href) => {
                // Skip external URLs
                if (/^https?:\/\//i.test(href)) return match;
                const key = href.replace(/^\.?\/?/, '');
                const entry = fs.get(key);
                if (entry) {
                    return `<style>${entry.content || ''}</style>`;
                }
                // Fallback: rewrite to absolute backend API URL so srcdoc iframe can fetch
                if (projectId && backendBaseUrl) {
                    return match.replace(href, `${backendBaseUrl}/api/projects/${projectId}/files/content?path=${encodeURIComponent(key)}`);
                }
                return match;
            }
        );
        // Inline JS: <script src="X.js"></script>
        html = html.replace(
            /<script\b[^>]*\bsrc=["']([^"']+\.js)["'][^>]*><\/script>/gi,
            (match, src) => {
                if (/^https?:\/\//i.test(src)) return match;
                const key = src.replace(/^\.?\/?/, '');
                const entry = fs.get(key);
                if (entry) {
                    return `<script>${entry.content || ''}</script>`;
                }
                // Fallback: rewrite to absolute backend API URL so srcdoc iframe can fetch
                if (projectId && backendBaseUrl) {
                    return match.replace(src, `${backendBaseUrl}/api/projects/${projectId}/files/content?path=${encodeURIComponent(key)}`);
                }
                return match;
            }
        );
        return html;
    };

    // Generate preview
    const generatePreview = () => {
        setLoading(true);
        setError(null);
        // Clear stale share link so the UI never flashes a previous project's URL
        setShareUrl(null);
        setShareExpiresAt(null);

        try {
            const fs = buildFileSystem();

            if (fs.size === 0) {
                setError('__empty__');
                setLoading(false);
                return;
            }

            // Find index.html - REQUIRED
            const indexFile = findIndexHtml(fs);

            if (!indexFile) {
                setError('No index.html found. Build failed to produce valid artifacts.');
                setLoading(false);
                return;
            }

            let html = indexFile.content || '';

            // If the HTML doesn't have <!DOCTYPE, it's probably incomplete
            if (!html.toLowerCase().includes('<!doctype')) {
                // Wrap it in a basic HTML structure
                html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Preview</title>
  ${indexFile.css ? `<style>${indexFile.css}</style>` : ''}
</head>
<body>
  ${html}
  ${indexFile.js ? `<script>${indexFile.js}</script>` : ''}
</body>
</html>`;
            } else {
                // Inject CSS/JS if provided separately and not already in HTML
                if (indexFile.css && !html.includes(indexFile.css.substring(0, 50))) {
                    html = html.replace('</head>', `<style>${indexFile.css}</style></head>`);
                }
                if (indexFile.js && !html.includes(indexFile.js.substring(0, 50))) {
                    html = html.replace('</body>', `<script>${indexFile.js}</script></body>`);
                }
            }

            // Inline local CSS/JS files so srcDoc preview renders them
            html = inlineLocalAssets(html, fs);

            // Intercept navigation to handle multi-page sites
            const navigationScript = `
<script>
(function() {
  // Intercept link clicks for multi-page navigation
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a');
    if (!link) return;
    // Use raw href attribute (not resolved .href) so relative links work in srcDoc
    var raw = link.getAttribute('href') || '';
    if (!raw || raw.startsWith('http') || raw.startsWith('//') || raw.startsWith('#') || raw.startsWith('mailto:')) return;
    if (raw.endsWith('.html') || raw === '/' || raw === '') {
      e.preventDefault();
      var path = raw.endsWith('.html') ? raw.replace(/^\.?\//, '') : 'index.html';
      window.parent.postMessage({ type: 'navigate', path: path }, '*');
    }
  });
  
  // Report errors to parent
  window.onerror = function(msg, url, line) {
    window.parent.postMessage({ 
      type: 'error', 
      message: msg + ' at line ' + line 
    }, '*');
    return false;
  };
})();
</script>`;

            // Insert navigation script before </body>
            if (html.includes('</body>')) {
                html = html.replace('</body>', navigationScript + '</body>');
            } else {
                html = html + navigationScript;
            }

            // Force desktop viewport so the narrow preview panel renders at
            // 1200px virtual width instead of mobile breakpoint (~450px).
            // The browser scales it down automatically — layout looks like desktop.
            html = html.replace(
                /<meta\s+name=["']viewport["'][^>]*\/?>/i,
                '<meta name="viewport" content="width=1200">'
            );
            if (!/<meta\s+name=["']viewport["']/i.test(html)) {
                html = html.replace(/(<head[^>]*>)/i, '$1\n<meta name="viewport" content="width=1200">');
            }

            setPreviewHtml(html);
            setLoading(false);

        } catch (err) {
            console.error('Preview generation error:', err);
            setError(err.message || 'Failed to generate preview');
            setLoading(false);
        }
    };

    // Handle navigation messages from iframe
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data.type === 'navigate') {
                const path = event.data.path.replace(/^\//, '');
                setCurrentPage(path);

                // Load the new page
                const fs = buildFileSystem();
                const pageFile = fs.get(path);

                if (pageFile) {
                    let html = pageFile.content || '';

                    if (!html.toLowerCase().includes('<!doctype')) {
                        html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Preview</title>
  ${pageFile.css ? `<style>${pageFile.css}</style>` : ''}
</head>
<body>
  ${html}
  ${pageFile.js ? `<script>${pageFile.js}</script>` : ''}
</body>
</html>`;
                    }

                    // Inline local CSS/JS files so srcDoc preview renders them
                    html = inlineLocalAssets(html, fs);

                    // Add navigation script
                    const navigationScript = `
<script>
(function() {
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a');
    if (!link) return;
    var raw = link.getAttribute('href') || '';
    if (!raw || raw.startsWith('http') || raw.startsWith('//') || raw.startsWith('#') || raw.startsWith('mailto:')) return;
    if (raw.endsWith('.html') || raw === '/' || raw === '') {
      e.preventDefault();
      var path = raw.endsWith('.html') ? raw.replace(/^\\.?\\//, '') : 'index.html';
      window.parent.postMessage({ type: 'navigate', path: path }, '*');
    }
  });
})();
</script>`;

                    if (html.includes('</body>')) {
                        html = html.replace('</body>', navigationScript + '</body>');
                    } else {
                        html = html + navigationScript;
                    }

                    setPreviewHtml(html);
                } else {
                    setError(`Page not found: ${path}`);
                }
            }

            if (event.data.type === 'error') {
                console.error('Preview error:', event.data.message);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [files, artifacts]);

    // Regenerate preview when files change
    useEffect(() => {
        generatePreview();
    }, [files, artifacts]);

    const handleRefresh = () => {
        setCurrentPage('/index.html');
        generatePreview();
    };

    // Get page list for navigation
    const getPageList = () => {
        const fs = buildFileSystem();
        return Array.from(fs.keys()).filter(p => p.endsWith('.html'));
    };

    // Build HTTP preview URL
    const getHttpPreviewUrl = useCallback((page = 'index.html') => {
        if (!projectId) return null;
        return `${backendBaseUrl}/api/projects/${projectId}/preview/${page}`;
    }, [projectId, backendBaseUrl]);

    const getActivePreviewUrl = useCallback(() => {
        if (shareUrl) return shareUrl;
        const normalized = (currentPage || '/index.html').replace(/^\//, '') || 'index.html';
        return getHttpPreviewUrl(normalized || 'index.html');
    }, [shareUrl, currentPage, getHttpPreviewUrl]);

    const activePreviewUrl = getActivePreviewUrl();

    // Download index.html for Direct HTML builds
    const [downloadingHtml, setDownloadingHtml] = useState(false);
    const handleDownloadHtml = async () => {
        if (!projectId) return;
        setDownloadingHtml(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(
                `${BACKEND_URL}/api/projects/${projectId}/export/html`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || `HTML download failed (${res.status})`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'index.html';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('index.html downloaded');
        } catch (err) {
            console.error('HTML download error:', err);
            toast.error(err.message || 'HTML download failed');
        } finally {
            setDownloadingHtml(false);
        }
    };

    // Export deploy-ready repo ZIP (Phase 2)
    const [exporting, setExporting] = useState(false);
    const handleExportRepo = async () => {
        if (!projectId) return;
        setExporting(true);
        try {
            const token = localStorage.getItem('token');
            const baseUrl = BACKEND_URL;
            const res = await fetch(
                `${baseUrl}/api/projects/${projectId}/export/repo`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${res.status})`);
            }
            const blob = await res.blob();
            const disposition = res.headers.get('content-disposition') || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `${projectId}.zip`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Repository ready for Vercel/Netlify');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    // Open preview in new browser tab
    const handleOpenInBrowser = () => {
        const url = activePreviewUrl;
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    // Copy live preview URL to clipboard
    const [copied, setCopied] = useState(false);
    const handleCopyUrl = useCallback(() => {
        const url = getActivePreviewUrl();
        if (url) {
            navigator.clipboard.writeText(url).then(() => {
                setCopied(true);
                toast.success('Live preview URL copied');
                setTimeout(() => setCopied(false), 2000);
            }).catch(() => toast.error('Failed to copy'));
        }
    }, [getActivePreviewUrl]);

    return (
        <div className="flex-1 min-h-0 w-full flex flex-col bg-slate-950" style={{ overflow: 'hidden' }}>
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-4 py-2.5 border-b border-slate-700/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                        <div className={`h-2.5 w-2.5 rounded-full ${error ? 'bg-red-500' : 'bg-green-500'} shadow-lg shadow-green-500/50`}></div>
                        <div className="h-2.5 w-2.5 rounded-full bg-yellow-500 opacity-60"></div>
                        <div className="h-2.5 w-2.5 rounded-full bg-red-500 opacity-60"></div>
                    </div>
                    <span className="text-sm font-medium text-slate-300">
                        Static Preview {currentPage && `- ${currentPage}`}
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    {/* Page selector */}
                    {getPageList().length > 1 && (
                        <div className="flex items-center gap-1">
                            {getPageList().map(page => (
                                <button
                                    key={page}
                                    onClick={() => {
                                        setCurrentPage(page);
                                        const fs = buildFileSystem();
                                        const pageFile = fs.get(page.replace(/^\//, ''));
                                        if (pageFile) {
                                            let html = pageFile.content || '';
                                            if (!html.toLowerCase().includes('<!doctype')) {
                                                html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Preview</title><style>${pageFile.css || ''}</style></head><body>${html}<script>${pageFile.js || ''}</script></body></html>`;
                                            }
                                            html = inlineLocalAssets(html, fs);
                                            setPreviewHtml(html);
                                        }
                                    }}
                                    className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${currentPage === page
                                        ? 'bg-blue-500/30 text-blue-300 border border-blue-500/40'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border border-transparent'
                                        }`}
                                >
                                    {page.replace('.html', '')}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Download HTML (Direct HTML builds) */}
                    {projectId && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDownloadHtml}
                            disabled={downloadingHtml}
                            className="text-slate-400 hover:text-white hover:bg-slate-700/50"
                            title="Download HTML"
                        >
                            <Download className={`h-4 w-4 ${downloadingHtml ? 'animate-pulse' : ''}`} />
                            <span className="ml-1 text-xs">HTML</span>
                        </Button>
                    )}

                    {/* Export Repo (Phase 2) */}
                    {projectId && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleExportRepo}
                            disabled={exporting}
                            className="text-slate-400 hover:text-white hover:bg-slate-700/50"
                            title="Export Repo (Deploy Ready)"
                        >
                            <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
                        </Button>
                    )}

                    {/* Open in Browser - HTTP Preview (Phase 1) */}
                    {projectId && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleOpenInBrowser}
                            className="text-slate-400 hover:text-white hover:bg-slate-700/50"
                            title="Open in browser (HTTP preview)"
                        >
                            <Globe className="h-4 w-4" />
                        </Button>
                    )}

                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={loading}
                        className="text-slate-400 hover:text-white hover:bg-slate-700/50"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Generator Beacon */}
            {previewHtml && (() => {
                const pick = (re, s) => { const m = s.match(re); return m ? m[1] : '?'; };
                const generator = pick(/data-generator="([^"]+)"/, previewHtml);
                const beaconPreset = pick(/data-preset="([^"]+)"/, previewHtml);
                const macro = pick(/data-layout-macro="(\d+)"/, previewHtml);
                const archetype = pick(/data-archetype="([^"]+)"/, previewHtml);
                const svgCount = (previewHtml.match(/<svg/gi) || []).length;
                return (
                    <div className="bg-slate-900/60 px-4 py-1 border-b border-slate-700/30 flex items-center">
                        <span className="text-[11px] text-slate-500 font-mono" style={{ opacity: 0.75 }}>
                            Generator: {generator} | preset: {beaconPreset} | macro: {macro} | archetype: {archetype} | svg: {svgCount}
                        </span>
                    </div>
                );
            })()}

            {/* Demo Mode Banner */}
            {DISABLE_SHARE_PREVIEW && (
                <div className="bg-amber-900/30 px-4 py-1.5 border-b border-amber-600/30 flex items-center gap-2">
                    <span className="text-[11px] text-amber-300">&#x1F6E1;&#xFE0F; Demo Mode: Share link disabled. Preview is rendered locally from generated files.</span>
                </div>
            )}

            {/* Live Preview URL Bar */}
            {projectId && !DISABLE_SHARE_PREVIEW && (
                <div className="bg-slate-900/80 px-4 py-1.5 border-b border-slate-700/40 flex items-center gap-2 flex-wrap">
                    <Globe className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                    <span
                        className="text-xs text-slate-400 truncate flex-1 font-mono cursor-pointer hover:text-slate-200 transition-colors"
                        onClick={handleOpenInBrowser}
                        title="Click to open in browser"
                    >
                        {activePreviewUrl || 'Preview link unavailable'}
                    </span>
                    {shareUrl && (
                        <span
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${shareAlmostExpired ? 'text-amber-300 border-amber-400/40' : 'text-emerald-300 border-emerald-400/40'} bg-white/5`}
                        >
                            {shareLoading ? 'Refreshing…' : (shareExpiryLabel ? `Link expires in ${shareExpiryLabel}` : 'Link active')}
                        </span>
                    )}
                    {shareError && !/not configured/i.test(shareError) && (
                        <span className="text-[11px] text-red-400 whitespace-nowrap">{shareError}</span>
                    )}
                    <button
                        onClick={() => projectId && fetchShareUrl(projectId)}
                        className="flex-shrink-0 text-slate-500 hover:text-white transition-colors p-0.5"
                        title="Refresh share link"
                        disabled={shareLoading}
                    >
                        <Link2 className={`h-3.5 w-3.5 ${shareLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        onClick={handleCopyUrl}
                        className="flex-shrink-0 text-slate-500 hover:text-white transition-colors p-0.5"
                        title="Copy URL"
                    >
                        {copied ? <CheckCheck className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                    <button
                        onClick={handleOpenInBrowser}
                        className="flex-shrink-0 text-slate-500 hover:text-white transition-colors p-0.5"
                        title="Open in new tab"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}

            {/* Content */}
            {error === '__empty__' ? (
                <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-slate-900 to-slate-950 p-6">
                    <div className="text-center max-w-md">
                        <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/30 inline-block mb-4">
                            <Globe className="h-10 w-10 text-slate-500" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-400 mb-2">Ready to Preview</h3>
                        <p className="text-sm text-slate-500">
                            Describe your site and hit Build to see the preview here.
                        </p>
                    </div>
                </div>
            ) : error ? (
                <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-red-950/30 to-slate-950 p-6">
                    <div className="text-center max-w-md">
                        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 inline-block mb-4">
                            <AlertCircle className="h-10 w-10 text-red-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-red-300 mb-2">Preview Failed</h3>
                        <p className="text-sm text-red-300/80 mb-4">{error}</p>
                        <p className="text-xs text-slate-500">
                            This means the build did not produce valid HTML artifacts.
                        </p>
                        <Button
                            onClick={handleRefresh}
                            className="mt-4 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600"
                            variant="outline"
                        >
                            Retry
                        </Button>
                    </div>
                </div>
            ) : loading ? (
                <div className="flex-1 flex items-center justify-center bg-slate-950">
                    <div className="text-center">
                        <RefreshCw className="h-8 w-8 text-slate-400 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-slate-400">Loading preview...</p>
                    </div>
                </div>
            ) : (
                <iframe
                    key={currentPage}
                    ref={iframeRef}
                    srcDoc={previewHtml}
                    className="flex-1 min-h-0 w-full border-0 bg-white"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                    title="Static Preview"
                    style={{ display: 'block', height: '100%', overflow: 'auto' }}
                />
            )}
        </div>
    );
};

export default StaticPreview;
